<script lang="ts">
  import type { SettlementDetails } from "../types";
  import Check from "~icons/ph/check-bold";
  import Info from "~icons/ph/info-bold";
  import OfferBreakdown from "../elements/OfferBreakdown.svelte";
  import {
    FaqHowCalculated,
    FaqWhenPayment,
    FaqHowPayment,
    FaqMultipleSettlements,
    FaqWhyFees,
    FaqTaxable,
  } from "../faqs/Faq";
  import FooterButtons from "../../../components/workflow/FooterButtons.svelte";
  import CloseButton from "../../../components/CloseButton.svelte";

  let {
    opposingPartyName,
    settlementDetails,
    jlgMainPhone,
    reopened,
    nextSettlementTaskUrl,
  }: {
    opposingPartyName: string;
    settlementDetails: SettlementDetails;
    jlgMainPhone: string;
    reopened: boolean;
    nextSettlementTaskUrl: string | null;
  } = $props();
</script>

<div class="relative isolate">
  {#if reopened}
    <h2 class="ui-title">We’ve shared your counteroffer with the finance company</h2>

    <p>We’ve sent your counteroffer to {opposingPartyName} and they’ll let us know whether they accept or reject it. We’ll notify you as soon as we hear back.</p>

    <p>There’s <b>no risk</b> of you getting a lower offer. You’ll either get the same offer or, ideally, a higher one.</p>
  {:else}
    <div
      class="pointer-events-none absolute -z-10 -m-4 inset-x-0 top-0 h-[313px] bg-gradient-to-b from-sky-200/40"
      aria-hidden="true">
    </div>

    <div class="flex justify-center py-6">
      <div class="bg-white w-16 h-16 rounded-full flex items-center justify-center shadow">
        <Check class="text-emerald-500 w-11 h-11" />
      </div>
    </div>

    <h2 class="ui-title">We’ll submit the counteroffer</h2>

    <p>Here’s what to expect:</p>

    <ol class="numbered-steps">
      <li><span>1</span>We’ll submit the counteroffer to the finance company</li>
      <li><span>2</span>The finance company will consider it and come back to us with a response</li>
      <li><span>3</span>We’ll inform you of their response ASAP</li>
    </ol>
  {/if}

  <p class="text-sm font-medium text-gray-600">This process typically takes 60-90 days, but can vary depending on the finance company’s processing speed.</p>

  <OfferBreakdown {settlementDetails} totalLabel="Initial offer" >
    <div class="ui-alert ui-alert-info">
      <Info />Offer was countered.
    </div>
  </OfferBreakdown>

  <h3 class="ui-section-title">Have questions?</h3>
  <div class="ui-accordion">
    <FaqHowCalculated />
    <FaqWhenPayment />
    <FaqHowPayment phone={jlgMainPhone} />
    <FaqMultipleSettlements />
    <FaqWhyFees gross={settlementDetails.grossSettlementAmount} />
    <FaqTaxable />
  </div>

  <FooterButtons back={false}>
    {#if nextSettlementTaskUrl}
      <CloseButton />
      <a href={nextSettlementTaskUrl} class="btn btn-primary" data-turbo-frame="task-modal">Next settlement</a>
    {:else}
      <CloseButton class="btn-primary w-full" />
    {/if}
  </FooterButtons>
</div>


<style lang="postcss">
  .numbered-steps {
    @apply flex flex-col gap-4;

    li {
      @apply flex flex-row gap-2;
    }

    li > span {
      @apply shrink-0 bg-gray-200 w-8 h-8 rounded-full inline-flex items-center justify-center font-bold -mt-1;
    }
  }
</style>
